/**
 * ImageTransform - Handles transform calculations and application
 * 
 * Responsibilities:
 * - Calculate base transforms for cover/contain modes
 * - Handle wrapper sizing and positioning
 * - Apply CSS transforms to wrapper
 * - Manage coordinate systems for zoom/pan
 */
export class ImageTransform {
    constructor(slideElement, imageFit = 'contain') {
        this.slideElement = slideElement;
        this.imageFit = imageFit;
        
        // Image properties
        this.imageNaturalWidth = 0;
        this.imageNaturalHeight = 0;
        
        // Positioning properties (relative to slide container)
        this.wrapperLeft = 0;
        this.wrapperTop = 0;
        this.wrapperWidth = 0;
        this.wrapperHeight = 0;
        
        // Zoom state
        this.currentZoom = 1;
        
        // Event listeners
        this.listeners = new Map();
        this.resizeObserver = null;
        
        this.setupResizeObserver();
    }
    
    /**
     * Add event listener
     */
    addEventListener(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    
    /**
     * Emit event to listeners
     */
    emit(event, data = {}) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(callback => callback(data));
        }
    }
    
    /**
     * Set up resize observer to recalculate on container size changes
     */
    setupResizeObserver() {
        this.resizeObserver = new ResizeObserver(() => {
            if (this.imageNaturalWidth && this.imageNaturalHeight) {
                this.calculateBaseTransform();
            }
        });
        
        this.resizeObserver.observe(this.slideElement);
    }
    
    /**
     * Set image dimensions
     */
    setImageDimensions(naturalWidth, naturalHeight) {
        this.imageNaturalWidth = naturalWidth;
        this.imageNaturalHeight = naturalHeight;
        this.calculateBaseTransform();
    }
    
    /**
     * Set image fit mode
     */
    setImageFit(imageFit) {
        this.imageFit = imageFit;
        if (this.imageNaturalWidth && this.imageNaturalHeight) {
            this.calculateBaseTransform();
        }
    }
    
    /**
     * Calculate base transform for cover/contain simulation
     */
    calculateBaseTransform() {
        this.calculateBaseTransformDimensions();
        
        this.emit('baseTransformCalculated', {
            wrapperWidth: this.wrapperWidth,
            wrapperHeight: this.wrapperHeight,
            wrapperLeft: this.wrapperLeft,
            wrapperTop: this.wrapperTop
        });
        
        // Apply the positioning
        this.applyTransform(true); // Skip transition for base calculation
    }
    
    /**
     * Calculate base transform dimensions without applying
     */
    calculateBaseTransformDimensions() {
        if (!this.imageNaturalWidth || !this.imageNaturalHeight) return;
        
        const rect = this.slideElement.getBoundingClientRect();
        // Skip if container has no dimensions yet
        if (rect.width === 0 || rect.height === 0) {
            // Retry after a frame
            requestAnimationFrame(() => this.calculateBaseTransform());
            return;
        }
        
        const imgAspectRatio = this.imageNaturalWidth / this.imageNaturalHeight;
        const containerAspectRatio = rect.width / rect.height;
        
        if (this.imageFit === 'cover') {
            // For cover: scale image to fill container, crop overflow
            if (imgAspectRatio > containerAspectRatio) {
                // Image is wider - scale to fit height, crop width
                this.wrapperHeight = rect.height;
                this.wrapperWidth = this.wrapperHeight * imgAspectRatio;
                // Center horizontally
                this.wrapperLeft = (rect.width - this.wrapperWidth) / 2;
                this.wrapperTop = 0;
            } else {
                // Image is taller - scale to fit width, crop height
                this.wrapperWidth = rect.width;
                this.wrapperHeight = this.wrapperWidth / imgAspectRatio;
                // Center vertically
                this.wrapperLeft = 0;
                this.wrapperTop = (rect.height - this.wrapperHeight) / 2;
            }
        } else {
            // For contain: scale image to fit inside container
            if (imgAspectRatio > containerAspectRatio) {
                // Image is wider - scale to fit width
                this.wrapperWidth = rect.width;
                this.wrapperHeight = this.wrapperWidth / imgAspectRatio;
                // Center vertically
                this.wrapperLeft = 0;
                this.wrapperTop = (rect.height - this.wrapperHeight) / 2;
            } else {
                // Image is taller - scale to fit height
                this.wrapperHeight = rect.height;
                this.wrapperWidth = this.wrapperHeight * imgAspectRatio;
                // Center horizontally
                this.wrapperLeft = (rect.width - this.wrapperWidth) / 2;
                this.wrapperTop = 0;
            }
        }
        
        // Apply zoom to the base dimensions
        this.applyZoom();
    }
    
    /**
     * Apply transform to image wrapper
     */
    applyTransform(skipTransition = false, imageWrapper = null, img = null) {
        // Get elements if not provided
        if (!imageWrapper) {
            imageWrapper = this.slideElement.querySelector('.carousel__image');
        }
        if (!img) {
            img = this.slideElement.querySelector('img');
        }
        
        if (!imageWrapper || !img || !this.wrapperWidth || !this.wrapperHeight) {
            return;
        }
        
        // Apply positioning and dimensions directly
        imageWrapper.style.position = 'absolute';
        imageWrapper.style.left = `${this.wrapperLeft}px`;
        imageWrapper.style.top = `${this.wrapperTop}px`;
        imageWrapper.style.width = `${this.wrapperWidth}px`;
        imageWrapper.style.height = `${this.wrapperHeight}px`;
        imageWrapper.style.transition = skipTransition ? 'none' : 'all 0.3s ease-out';
        
        // Image fills the wrapper naturally
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = this.imageFit === 'cover' ? 'cover' : 'contain';
        img.style.display = 'block';
        
        this.emit('transformApplied', {
            wrapperLeft: this.wrapperLeft,
            wrapperTop: this.wrapperTop,
            wrapperWidth: this.wrapperWidth,
            wrapperHeight: this.wrapperHeight
        });
    }
    
    /**
     * Set zoom level
     */
    setZoom(zoom, centerX = 0.5, centerY = 0.5) {
        if (!this.wrapperWidth || !this.wrapperHeight) return;
        
        const rect = this.slideElement.getBoundingClientRect();
        
        // Convert center point from normalized to actual pixels in container
        const centerPixelX = centerX * rect.width;
        const centerPixelY = centerY * rect.height;
        
        // Calculate the point in the current wrapper that corresponds to the center
        const pointInWrapperX = centerPixelX - this.wrapperLeft;
        const pointInWrapperY = centerPixelY - this.wrapperTop;
        
        // Store old zoom for ratio calculation
        const oldZoom = this.currentZoom;
        this.currentZoom = zoom;
        
        // Calculate new dimensions
        const newWidth = (this.wrapperWidth / oldZoom) * zoom;
        const newHeight = (this.wrapperHeight / oldZoom) * zoom;
        
        // Calculate new position to keep the center point stationary
        const scaleRatio = zoom / oldZoom;
        const newPointInWrapperX = pointInWrapperX * scaleRatio;
        const newPointInWrapperY = pointInWrapperY * scaleRatio;
        
        // Update wrapper properties
        this.wrapperWidth = newWidth;
        this.wrapperHeight = newHeight;
        this.wrapperLeft = centerPixelX - newPointInWrapperX;
        this.wrapperTop = centerPixelY - newPointInWrapperY;
        
        // Apply pan limits
        this.applyPanLimits();
        
        this.applyTransform(false);
        
        this.emit('zoomChanged', {
            zoom: this.currentZoom,
            isZoomed: zoom > 1
        });
    }
    
    /**
     * Apply zoom to base dimensions
     */
    applyZoom() {
        if (this.currentZoom !== 1) {
            const baseWidth = this.wrapperWidth;
            const baseHeight = this.wrapperHeight;
            
            this.wrapperWidth = baseWidth * this.currentZoom;
            this.wrapperHeight = baseHeight * this.currentZoom;
            
            // Adjust position to keep image centered when zooming from base
            const rect = this.slideElement.getBoundingClientRect();
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            
            this.wrapperLeft = centerX - (this.wrapperWidth / 2);
            this.wrapperTop = centerY - (this.wrapperHeight / 2);
        }
    }
    
    /**
     * Pan by delta amounts
     */
    pan(deltaX, deltaY) {
        this.wrapperLeft += deltaX;
        this.wrapperTop += deltaY;
        
        // Apply limits
        this.applyPanLimits();
        
        // Apply transform immediately for responsive panning
        this.applyTransform(true);
        
        this.emit('panChanged', {
            wrapperLeft: this.wrapperLeft,
            wrapperTop: this.wrapperTop
        });
    }
    
    /**
     * Apply pan limits to prevent showing background
     */
    applyPanLimits() {
        if (!this.wrapperWidth || !this.wrapperHeight) return;
        
        const rect = this.slideElement.getBoundingClientRect();
        
        // Calculate limits to prevent showing empty space
        // Wrapper should not move so far that it shows empty space
        
        // Left limit: wrapper's right edge should not go past container's left edge
        const minLeft = -(this.wrapperWidth - rect.width);
        // Right limit: wrapper's left edge should not go past 0
        const maxLeft = 0;
        
        // Top limit: wrapper's bottom edge should not go past container's top edge
        const minTop = -(this.wrapperHeight - rect.height);
        // Bottom limit: wrapper's top edge should not go past 0
        const maxTop = 0;
        
        // For contain mode, also ensure wrapper doesn't move outside when smaller than container
        if (this.imageFit === 'contain') {
            // If wrapper is smaller than container, keep it within bounds
            if (this.wrapperWidth <= rect.width) {
                this.wrapperLeft = Math.max(0, Math.min(rect.width - this.wrapperWidth, this.wrapperLeft));
            } else {
                this.wrapperLeft = Math.max(minLeft, Math.min(maxLeft, this.wrapperLeft));
            }
            
            if (this.wrapperHeight <= rect.height) {
                this.wrapperTop = Math.max(0, Math.min(rect.height - this.wrapperHeight, this.wrapperTop));
            } else {
                this.wrapperTop = Math.max(minTop, Math.min(maxTop, this.wrapperTop));
            }
        } else {
            // For cover mode, apply limits normally
            this.wrapperLeft = Math.max(minLeft, Math.min(maxLeft, this.wrapperLeft));
            this.wrapperTop = Math.max(minTop, Math.min(maxTop, this.wrapperTop));
        }
    }
    
    /**
     * Reset zoom and pan
     */
    resetZoom() {
        this.currentZoom = 1;
        
        // Recalculate dimensions without applying (to avoid skipTransition)
        this.calculateBaseTransformDimensions();
        
        // Apply the transform with smooth transition
        this.applyTransform(false);
        
        this.emit('zoomChanged', {
            zoom: this.currentZoom,
            isZoomed: false
        });
    }
    
    /**
     * Check if manual panning is allowed (only when zoomed)
     */
    canPan() {
        // Manual panning only allowed when zoomed in
        return this.currentZoom > 1 && this.canAutoPan();
    }
    
    /**
     * Check if auto-panning is allowed (based on image size vs container)
     */
    canAutoPan() {
        const rect = this.slideElement.getBoundingClientRect();
        // Can auto-pan if wrapper is larger than container in any dimension
        return this.wrapperWidth > rect.width || this.wrapperHeight > rect.height;
    }
    
    /**
     * Get current transform state
     */
    getState() {
        return {
            currentZoom: this.currentZoom,
            wrapperLeft: this.wrapperLeft,
            wrapperTop: this.wrapperTop,
            isZoomed: this.currentZoom > 1,
            canPan: this.canPan(),
            wrapperWidth: this.wrapperWidth,
            wrapperHeight: this.wrapperHeight
        };
    }
    
    /**
     * Clean up resources
     */
    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.listeners.clear();
    }
}